import Box from '@mui/material/Box'
import Typography from '@mui/material/Typography'
import React, { forwardRef, ReactNode, useImperativeHandle, useMemo } from 'react'
import { DropzoneOptions, useDropzone } from 'react-dropzone'

import DragDropFocusBg from '../images/DragDropFocusBg.png'
import { LuneTheme } from '../theme'
import { staticImagePath } from '../utils'

const baseStyle = {
    borderRadius: '24px',
    backgroundColor: LuneTheme.palette.White,
    height: '100%',
    width: '100%',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    alignSelf: 'stretch',
    gap: '10px',

    /* dashed border */
    backgroundImage: `url("data:image/svg+xml,%3csvg width='100%25' height='100%25' xmlns='http://www.w3.org/2000/svg'%3e%3crect width='100%25' height='100%25' fill='none' rx='24' ry='24' stroke='rgb(224,224,224)' stroke-width='2' stroke-dasharray='24%2c24' stroke-dashoffset='0' stroke-linecap='butt'/%3e%3c/svg%3e")`,
}

const uploadStyle = {
    backgroundImage: 'unset',
    backgroundColor: 'unset',
}

const rejectedStyle = {
    backgroundColor: `#F8E9E9`,
    height: '100%',
    width: '100%',
    borderRadius: '24px',

    /* dashed border */
    backgroundImage: `url("data:image/svg+xml,%3csvg width='100%25' height='100%25' xmlns='http://www.w3.org/2000/svg'%3e%3crect width='100%25' height='100%25' fill='none' rx='24' ry='24' stroke='%23D32F2F' stroke-width='8' stroke-dasharray='24%2c24' stroke-dashoffset='0' stroke-linecap='butt'/%3e%3c/svg%3e")`,
}

const BlendedBorder = () => (
    <Box
        sx={{
            position: 'absolute',
            height: '100%',
            width: '100%',
            backgroundImage: `url("data:image/svg+xml,%3csvg width='100%25' height='100%25' xmlns='http://www.w3.org/2000/svg'%3e%3crect width='100%25' height='100%25' fill='none' rx='24' ry='24' stroke='%23000000' stroke-width='8' stroke-dasharray='24%2c24' stroke-dashoffset='0' stroke-linecap='butt'/%3e%3c/svg%3e")`,
            mixBlendMode: 'overlay',
        }}
    />
)

export interface DragAndDropFileUploadProps extends DropzoneOptions {
    description?: ReactNode
    children?: React.ReactNode
    isUploading?: boolean
    rejectDescription?: string
    colorful?: boolean
    /**
     * Fallback text shown while upload is in progress.
     * Ignored when `description` is explicitly provided.
     *
     * This may be plain text or richer React content. Defaults to "Uploading...".
     */
    uploadingDescription?: ReactNode
    /**
     * Fallback text shown when waiting for drag-and-drop.
     * Ignored when `description` is explicitly provided.
     *
     * This may be plain text or richer React content. Defaults to "Drop your file here".
     */
    dropDescription?: ReactNode
}

const DragAndDropFileUpload = forwardRef<
    Pick<HTMLInputElement, 'click'>,
    DragAndDropFileUploadProps
    // eslint-disable-next-line complexity
>((props, ref) => {
    const { palette } = LuneTheme
    const { isUploading, children, colorful } = props

    const {
        getRootProps,
        getInputProps,
        isFileDialogActive,
        isDragAccept,
        isDragReject,
        inputRef,
    } = useDropzone({
        ...props,
        ...(isUploading ? { disabled: true } : {}), // disable new file isUploading if a file is being uploaded
        maxFiles: 1, // for now, limit to 1 file at the time
    })

    useImperativeHandle(
        ref,
        () => ({
            click: () => {
                inputRef.current?.click()
            },
        }),
        [inputRef],
    )

    const isDragging = useMemo(
        () => isFileDialogActive || isDragAccept,
        [isFileDialogActive, isDragAccept],
    )

    const showFancy = (isDragging && !isUploading) || !!colorful

    const rootSx = useMemo(
        () => ({
            ...baseStyle,
            ...(showFancy ? uploadStyle : {}),
            ...(isDragReject && !isUploading ? rejectedStyle : {}),
            position: 'relative',
            overflow: 'hidden',
            borderRadius: '24px',
            p: 2,
            width: '100%',
            backgroundColor: palette.Grey50,
            boxSizing: 'border-box',
            '*': {
                boxSizing: 'border-box',
            },
        }),
        [showFancy, isDragReject, isUploading],
    )

    return (
        <Box {...getRootProps()} sx={rootSx}>
            <input {...getInputProps()} />

            {showFancy && (
                <Box
                    sx={{
                        position: 'absolute',
                        inset: 0,
                        borderRadius: '24px',
                        backgroundSize: 'cover',
                        backgroundImage: colorful ? 'unset' : `url(${DragDropFocusBg})`,
                        border: 'solid 1px #ffffff',
                        boxSizing: 'border-box',
                    }}
                >
                    {colorful ? (
                        <Box
                            sx={{
                                position: 'absolute',
                                inset: 0,
                                backgroundImage: `url(${staticImagePath(DragDropFocusBg)})`,
                                opacity: isDragging && !isUploading ? 1 : 0.6,
                            }}
                        >
                            <BlendedBorder />
                            <BlendedBorder />
                            <BlendedBorder />
                        </Box>
                    ) : (
                        <>
                            <BlendedBorder />
                            <BlendedBorder />
                        </>
                    )}
                </Box>
            )}

            <Box
                sx={{
                    position: 'relative',
                    zIndex: 1,
                    height: '100%',
                    width: '100%',
                    borderRadius: '24px',
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    '> p': {
                        width: '100%',
                    },
                }}
            >
                {children || (
                    <>
                        {isDragReject && props.rejectDescription ? (
                            <Typography
                                sx={{ color: palette.Red500, textAlign: 'center' }}
                                variant="body3"
                            >
                                {props.rejectDescription}
                            </Typography>
                        ) : (
                            <Typography
                                sx={{
                                    color:
                                        colorful || isDragging ? palette.Grey900 : palette.Grey400,
                                }}
                                variant="body1"
                            >
                                {props.description
                                    ? props.description
                                    : !isDragReject && isUploading
                                      ? (props.uploadingDescription ?? 'Uploading...')
                                      : (props.dropDescription ?? 'Drop your file here')}
                            </Typography>
                        )}
                    </>
                )}
            </Box>
        </Box>
    )
})

// Resolves `error    Component definition is missing display name  react/display-name`
// https://stackoverflow.com/questions/67992894/component-definition-is-missing-display-name-for-forwardref
DragAndDropFileUpload.displayName = 'DragAndDropFileUpload'

export default DragAndDropFileUpload
